import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import {
  BatchSetting,
  ProductSetting,
  ProductChangeover,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'

const fetchProductSettings = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/product-settings?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
  })
  return data
}

export const useProductSettings = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<ProductSetting[]> => {
  return useQuery({
    queryKey: ['product-settings', projectId, scenarioId],
    queryFn: () => fetchProductSettings(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

const fetchBatchSettings = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/batch-settings?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useBatchSettings = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}) => {
  return useQuery({
    queryKey: ['batch-settings', projectId, scenarioId],
    queryFn: () => fetchBatchSettings(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

const fetchProductChangeovers = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useProductChangeovers = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}) => {
  return useQuery({
    queryKey: ['product-changeovers', projectId, scenarioId],
    queryFn: () => fetchProductChangeovers(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

// MUTATION TO UPDATE THE DELTA TABLES
// SEND THE ENTIRE NEW STATE TO GET SAVED
export interface UpdateSettingsInterface<T> {
  changedRows: Record<string, T>
  projectId: string
  scenarioId: string
}

const updateSettings = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateSettingsInterface<ProductSetting>) => {
  const { data } = await requestV3({
    url: `/product-settings?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

export const useUpdateSettings = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateSettings,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

const updateBatchSettings = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateSettingsInterface<BatchSetting>) => {
  const { data } = await requestV3({
    url: `/batch-settings?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

export const useUpdateBatchSettings = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateBatchSettings,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

const updateProductChangeovers = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateSettingsInterface<ProductChangeover>) => {
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

const uploadBatchSettings = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/batch-settings?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadBatchSettings = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadBatchSettings,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

const uploadProductSettings = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/product-settings?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadProductSettings = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadProductSettings,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

const uploadProductChangeovers = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadProductChangeovers = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadProductChangeovers,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-changeovers', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}

export const useUpdateProductChangeovers = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateProductChangeovers,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-changeovers', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}
